import {
  chainPromises,
  images,
  niceScroll,
  scrolling,
} from 'assets/utilities';

import {
  cartModel,
  footer,
  funnelHeader,
  header,
  headerPromo,
} from 'assets/components';

import './funnel.scss';

const INITIAL_STATE = {
  addOns: [],
  color: '',
  frequency: '',
  isLoading: false,
  pack: '',
  size: '',
  viewProps: {},
};

let state = { ...INITIAL_STATE };

const getState = function getFunnelState() {
  return { ...state };
};

const getHasFrequency = function getFunnelHasFrequency() {
  return document.querySelectorAll('.js-funnelOption[data-option="frequency"]').length;
}

const getVariants = function getFunnelVariants() {
  const scriptEl = document.querySelector('.js-funnelData');
  const variants = JSON.parse(scriptEl.innerHTML);

  return variants;
};

const getIncrement = function getFunnelProgressIncrement() {
  return Math.floor(100 / (document.querySelectorAll('.js-funnelStep').length + 1 || 1));
};

const getActiveStep = function getActiveFunnelStepIndex({
  color, frequency, pack, size,
}) {
  if (frequency && size && (color || pack)) {
    return 3;
  }

  if (frequency && size) {
    return 2;
  }

  if (frequency) {
    return 1;
  }

  return 0;
};

const updateUrlStep = function(stepNumber) {
  let searchParam = '';
  let stepName = '';

  if (stepNumber === 1) {
    searchParam = '?step=select-size';
    stepName = 'Select Size';
  } else if (stepNumber === 2) {
    searchParam = '?step=select-quantity';
    stepName = 'Select Quantity';
  } else if (stepNumber === 3) {
    searchParam = '?step=select-add-ons';
    stepName = 'Select Add-ons'
  }

  const newUrl = window.location.origin + window.location.pathname + searchParam;
  window.history.pushState({}, newUrl, newUrl);

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    'event': 'build_your_kit_step',
    'step': stepName,
    'page': window.location.pathname + searchParam
  });
};

const render = function renderFunnel() {
  const { addOns } = getState();
  const activeStepIndex = getActiveStep(getState());

  updateUrlStep(activeStepIndex);

  funnelHeader.setProgress((activeStepIndex + 1) * getIncrement());

  [].forEach.call(
    document.querySelectorAll('.js-funnelStep'),
    (el, index) => {
      el.setAttribute('aria-hidden', index === activeStepIndex ? 'false' : 'true');
    },
  );

  [].forEach.call(
    document.querySelectorAll('.js-funnelAddOn'),
    (el) => {
      const variantId = el.getAttribute('data-id');
      const isAdded = addOns.indexOf(variantId) !== -1;
      el.querySelector('.js-funnelAddOnAdd').setAttribute('aria-hidden', isAdded ? 'true' : 'false');
      el.querySelector('.js-funnelAddOnRemove').setAttribute('aria-hidden', !isAdded ? 'true' : 'false');
      el.setAttribute('data-added', isAdded ? 'true' : 'false');
    },
  );

  const hasAddOns = !!addOns.length;
  document.querySelector('.js-funnelProceed').setAttribute('aria-hidden', !hasAddOns ? 'true' : 'false');
  document.querySelector('.js-funnelSkip').setAttribute('aria-hidden', hasAddOns ? 'true' : 'false');
};

const setState = function setFunnelState(newState) {
  const prevActiveStateIndex = getActiveStep(state);

  state = {
    ...state,
    ...newState,
  };

  const activeStepIndex = getActiveStep(state);

  render();

  if (activeStepIndex !== prevActiveStateIndex) {
    if (scrolling.getScrollY() > 50) {
      niceScroll.scrollTo({ speed: 0.6, y: 0 });
    }

    document.querySelectorAll('.js-funnelStep')[activeStepIndex]
      .querySelector('.js-funnelHeading').focus({ preventScroll: true });
  }
};

const onOptionClick = function onFunnelOptionClick(ev) {
  if (getState().isLoading) {
    return;
  }

  const el = ev.currentTarget;
  const option = el.getAttribute('data-option');
  const value = el.getAttribute('data-value');

  setState({
    [option]: value,
  });
};

const getVariantId = function getFunnelVariantId({ color, pack, size }) {
  const variant = getVariants()
    .filter(item => item)
    .find((item) => {
      const options = item.options.map(option => option.toLowerCase());

      if (pack && size && !color) {
        return (
          options.indexOf(pack.toLowerCase()) !== -1
          && options.indexOf(size.toLowerCase()) !== -1
        );
      }

      // No color step, no pack
      if (!color && !pack) {
        return options.indexOf(size.toLowerCase()) !== -1;
      }

      // Color & size, no pack
      if (!pack && size && color) {
        return (
          options.indexOf(color.toLowerCase()) !== -1
          && options.indexOf(size.toLowerCase()) !== -1
        );
      }

      return (
        options.indexOf(color.toLowerCase()) !== -1
        && options.indexOf(size.toLowerCase()) !== -1
        && options.indexOf(pack.toLowerCase()) !== -1
      );
    });

  return variant ? variant.id : '';
};

const goToCart = function goToCartFromFunnel() {
  getState().viewProps.changeState('/cart');
};

const addToCart = function addToCartFromFunnel(variantId) {
  const id = variantId || getVariantId(state);
  setState({ isLoading: true });

  if (!id) {
    // IDEA: add error message handling
    alert('Sorry, couldn\'t find a product matching your selection. Please try again.');
    setState({ isLoading: false });
    return;
  }

  return cartModel.add({
    id,
    properties: {
      shipping_interval_frequency: getState().frequency,
      shipping_interval_unit_type: cartModel.getFrequencyUnit(),
    },
  })
    .then(() => {
      setState({ isLoading: false });
    });
};

const onAddOnClick = function onFunnelAddOnClick(ev) {
  const addOnId = ev.currentTarget.getAttribute('data-id');
  const { addOns } = getState();
  const addedIndex = addOns.indexOf(addOnId);

  if (addedIndex !== -1) {
    addOns.splice(addedIndex, 1);
    setState({ addOns });
  } else {
    addOns.push(addOnId);
    setState({ addOns });
  }
};

const onSkipClick = function onFunnelSkipClick() {
  addToCart()
    .then(goToCart);
};

const onProceedClick = function onFunnelProceedClick() {
  const { addOns } = getState();
  const addOnPromises = addOns.map(id => () => addToCart(id));

  addToCart()
    .then(() => chainPromises(addOnPromises))
    .then(goToCart);
};

const onBackClick = function onFunnelBackClick(e) {
  e.preventDefault();

  if (getState().isLoading) {
    return;
  }

  const activeStepIndex = getActiveStep(state);
  const { color, pack } = state;

  if (activeStepIndex === 4) {
    setState({
      color: '',
      pack: '',
    });
    return;
  }

  if (activeStepIndex === 3 && color) {
    setState({
      color: '',
    });
    return;
  }

  if (activeStepIndex === 3 && pack) {
    setState({
      pack: '',
    });
    return;
  }

  if (activeStepIndex === 2) {
    setState({
      size: '',
    });
    return;
  }

  if (activeStepIndex === 1 && getHasFrequency()) {
    setState({
      frequency: '',
    });
    return;
  }

  window.history.back();
};

const bind = function bindFunnel(props = {}) {
  const method = props.unbind ? 'removeEventListener' : 'addEventListener';

  [].forEach.call(
    document.querySelectorAll('.js-funnelOption'),
    (el) => {
      el[method]('click', onOptionClick);
    },
  );

  [].forEach.call(
    document.querySelectorAll('.js-funnelBack'),
    (el) => {
      el[method]('click', onBackClick);
    },
  );

  [].forEach.call(
    document.querySelectorAll('.js-funnelAddOn'),
    (el) => {
      el[method]('click', onAddOnClick);
    },
  );

  [].forEach.call(
    document.querySelectorAll('.js-funnelSkip'),
    (el) => {
      el[method]('click', onSkipClick);
    },
  );

  [].forEach.call(
    document.querySelectorAll('.js-funnelProceed'),
    (el) => {
      el[method]('click', onProceedClick);
    },
  );
};

const destroy = function destroyFunnel() {
  bind({ unbind: true });
  header.show();
  footer.show();
  funnelHeader.destroy();

  state = {
    ...INITIAL_STATE,
  };
};

const init = function initFunnel(viewProps) {
  state = { ...INITIAL_STATE };
  if (!getHasFrequency()) {
    state.frequency = cartModel.getDefaultFrequency();
  }

  headerPromo.hide();
  setState({ viewProps });
  window.scrollTo(0, 0);
  scrolling.rebuildCache();
  images.init();
  header.hide({ shouldIncludeMobile: true });
  footer.hide();

  funnelHeader.init();
  funnelHeader.setProgress(getIncrement());

  bind();
};

export default {
  destroy,
  init,
};
